/** Interface text for the export page. Keys are prefixed "exports."; every English key needs its Serbian counterpart. */
export const en = {
  'exports.title': 'Data explorer and exports',
  'exports.description': 'Pre-aggregated datasets only. Every export is checked on the server, logged with its purpose, and never contains an account.',
  'exports.externalDisabled': 'External delivery disabled',
  'exports.externalDisabledText': 'Commercial exports stay off until a release policy and a privacy and legal review are approved. Customer-derived datasets are never released externally.',

  // Builder
  'exports.builder.title': 'Export builder',
  'exports.builder.description': 'Choose a dataset, a period and a format. Large exports continue in the background; the file stays available to you for download.',
  'exports.field.dataset': 'Dataset',
  'exports.field.from': 'From',
  'exports.field.to': 'To',
  'exports.field.format': 'Format',
  'exports.field.audience': 'Audience',
  'exports.field.category': 'Category',
  'exports.field.retailer': 'Chain',
  'exports.field.purpose': 'Purpose (logged with the export)',
  'exports.field.purposePlaceholder': 'Why this export is needed and who will use it',
  'exports.audience.internal': 'Internal',
  'exports.audience.commercial': 'Commercial (external)',
  'exports.filter.allCategories': 'All categories',
  'exports.filter.allRetailers': 'All chains',
  'exports.filter.allCombined': 'Overall index (all categories together)',
  'exports.filter.search': 'Type to search',
  'exports.filter.noMatch': 'No match',
  'exports.filter.fromData': 'The list comes from the data: only values that exist can be chosen.',
  'exports.format.csv': 'CSV',
  'exports.format.csv_excel': 'CSV for Excel',
  'exports.format.xlsx': 'XLSX',
  'exports.format.json': 'JSON',
  'exports.formatHelp.csv': 'CSV: UTF-8 with commas and decimal points, for other programs and scripts.',
  'exports.formatHelp.csv_excel': 'CSV for Excel: for Excel with Serbian settings (semicolons, decimal comma, č ć š ž đ shown correctly).',
  'exports.formatHelp.xlsx': 'XLSX: an Excel workbook with a sheet that describes the data, including each chain\'s freshness and licence for retail data.',
  'exports.formatHelp.json': 'JSON: for programs, with the description of the data included.',
  'exports.columnsNote': 'Column names stay in English in every file so programs can read them; their meaning is listed here.',
  'exports.disclosureNote': 'Small groups are suppressed and counts rounded on the server before the file is built.',
  'exports.create': 'Create export',
  'exports.building': 'Building',
  'exports.neverCommercial': 'This dataset is never released for commercial use.',
  'exports.commercialDisabled': 'Commercial release is disabled until the legal review is approved.',
  'exports.failedTitle': 'Export refused or failed',
  'exports.failed': 'The export failed.',
  'exports.downloadFailed': 'Download failed',

  // Dataset details
  'exports.details.title': 'About this dataset',
  'exports.details.columns': 'Columns',
  'exports.details.column': 'Column',
  'exports.details.type': 'Type',
  'exports.details.meaning': 'Meaning',
  'exports.details.range': 'Available data',
  'exports.details.rangeValue': '{first} to {last}',
  'exports.details.periods': 'Stored',
  'exports.details.fresh': 'Last rebuilt',
  'exports.details.noData': 'No data yet',
  'exports.details.unknown': 'Unknown',
  'exports.type.string': 'text',
  'exports.type.number': 'number',
  'exports.type.date': 'date',
  'exports.type.boolean': 'yes or no',
  'exports.periods.snapshot_one': '{count} snapshot',
  'exports.periods.snapshot_few': '{count} snapshots',
  'exports.periods.snapshot_other': '{count} snapshots',
  'exports.periods.week_one': '{count} week',
  'exports.periods.week_few': '{count} weeks',
  'exports.periods.week_other': '{count} weeks',
  'exports.periods.month_one': '{count} month',
  'exports.periods.month_few': '{count} months',
  'exports.periods.month_other': '{count} months',
  'exports.periods.day_one': '{count} day',
  'exports.periods.day_few': '{count} days',
  'exports.periods.day_other': '{count} days',

  // Warnings and estimate
  'exports.warn.beforeFirst': 'The data starts on {date}, so the part of the period before that day has no data.',
  'exports.warn.beforeFirstSnapshot': 'Historical snapshots before {date} do not exist yet: the weekly snapshot job started then. The file will hold only what exists.',
  'exports.warn.oneSnapshot': 'Only one snapshot exists ({date}). Trends and comparisons over time need at least two snapshots, which are added weekly.',
  'exports.warn.outside': 'The selected period has no data. Available: {first} to {last}.',
  'exports.warn.afterLast': 'The newest data is from {date}.',
  'exports.estimate': 'Estimated rows: {rows}',
  'exports.estimateLoading': 'Estimating rows',
  'exports.estimateTooMany': 'Estimated rows: {rows}. A single export holds at most {max}; narrow the period or add a filter.',

  // Result
  'exports.result.running': 'Running in the background. You can leave this page; the export appears in the history when it finishes.',
  'exports.result.failed': 'The export failed.',
  'exports.result.failedReason': 'The export failed: {reason}.',
  'exports.result.suppressed': ', {cells} cells suppressed',
  'exports.result.timing': 'Server time {server} ms',
  'exports.result.timingHere': ', ready in {ms} ms here',
  'exports.download': 'Download',

  // Datasets table
  'exports.datasets.title': 'Datasets',
  'exports.datasets.description': 'What each dataset holds, where it comes from and who may receive it.',
  'exports.datasets.count_one': '{count} dataset available to your role',
  'exports.datasets.count_few': '{count} datasets available to your role',
  'exports.datasets.count_other': '{count} datasets available to your role',
  'exports.col.dataset': 'Dataset',
  'exports.col.class': 'Class',
  'exports.col.grain': 'Grain',
  'exports.col.disclosure': 'Disclosure control',
  'exports.col.commercial': 'Commercial',
  'exports.col.source': 'Source',
  'exports.col.licence': 'Licence',
  'exports.class.catalog': 'Retail catalog',
  'exports.class.customer_derived': 'Customer-derived',
  'exports.class.operational': 'Operational',
  'exports.grain.snapshot': 'snapshot',
  'exports.grain.week': 'week',
  'exports.grain.month': 'month',
  'exports.grain.day': 'day',
  'exports.commercial.eligible': 'eligible',
  'exports.commercial.afterReview': 'eligible after review',
  'exports.commercial.never': 'never',

  // History
  'exports.history.title': 'History and audit log',
  'exports.history.all': 'Every export request, refusal and download by every user.',
  'exports.history.own': 'Your export requests, refusals and downloads.',
  'exports.history.empty': 'No exports yet.',
  'exports.history.when': 'When',
  'exports.history.event': 'Event',
  'exports.history.dataset': 'Dataset',
  'exports.history.audience': 'Audience',
  'exports.history.format': 'Format',
  'exports.history.rows': 'Rows',
  'exports.history.user': 'User',
  'exports.history.purpose': 'Purpose',
  'exports.event.requested': 'requested',
  'exports.event.completed': 'completed',
  'exports.event.failed': 'failed',
  'exports.event.downloaded': 'downloaded',
  'exports.event.refused': 'refused',
};

export const sr: Record<keyof typeof en, string> = {
  'exports.title': 'Pregled podataka i izvoz',
  'exports.description': 'Samo unapred agregirani skupovi. Svaki izvoz se proverava na serveru, beleži sa svrhom i nikada ne sadrži nalog.',
  'exports.externalDisabled': 'Isporuka van Aruhe je isključena',
  'exports.externalDisabledText': 'Komercijalni izvozi ostaju isključeni dok se ne odobre pravila izdavanja i provera privatnosti i pravna provera. Skupovi izvedeni iz korisničkih podataka se nikada ne izdaju van Aruhe.',

  'exports.builder.title': 'Izrada izvoza',
  'exports.builder.description': 'Izaberi skup podataka, period i format. Veliki izvozi se nastavljaju u pozadini, a fajl ostaje dostupan za preuzimanje.',
  'exports.field.dataset': 'Skup podataka',
  'exports.field.from': 'Od',
  'exports.field.to': 'Do',
  'exports.field.format': 'Format',
  'exports.field.audience': 'Namena',
  'exports.field.category': 'Kategorija',
  'exports.field.retailer': 'Trgovinski lanac',
  'exports.field.purpose': 'Svrha (beleži se uz izvoz)',
  'exports.field.purposePlaceholder': 'Zašto je izvoz potreban i ko će ga koristiti',
  'exports.audience.internal': 'Interno',
  'exports.audience.commercial': 'Komercijalno (van Aruhe)',
  'exports.filter.allCategories': 'Sve kategorije',
  'exports.filter.allRetailers': 'Svi trgovinski lanci',
  'exports.filter.allCombined': 'Ukupan indeks (sve kategorije zajedno)',
  'exports.filter.search': 'Kucaj za pretragu',
  'exports.filter.noMatch': 'Nema rezultata',
  'exports.filter.fromData': 'Lista dolazi iz podataka: mogu se izabrati samo vrednosti koje postoje.',
  'exports.format.csv': 'CSV',
  'exports.format.csv_excel': 'CSV za Excel',
  'exports.format.xlsx': 'XLSX',
  'exports.format.json': 'JSON',
  'exports.formatHelp.csv': 'CSV: UTF-8 sa zarezima i decimalnom tačkom, za druge programe i skripte.',
  'exports.formatHelp.csv_excel': 'CSV za Excel: za Excel sa srpskim podešavanjima (tačka-zarez, decimalni zarez, ispravna slova č ć š ž đ).',
  'exports.formatHelp.xlsx': 'XLSX: Excel radna sveska sa listom koji opisuje podatke, za maloprodajne podatke i ažurnost i licencu svakog lanca.',
  'exports.formatHelp.json': 'JSON: za programe, sa opisom podataka u fajlu.',
  'exports.columnsNote': 'Nazivi kolona ostaju na engleskom u svakom fajlu da bi ih programi čitali; njihovo značenje je navedeno ovde.',
  'exports.disclosureNote': 'Male grupe se prikrivaju, a brojevi zaokružuju na serveru pre izrade fajla.',
  'exports.create': 'Napravi izvoz',
  'exports.building': 'Izrada u toku',
  'exports.neverCommercial': 'Ovaj skup podataka se nikada ne izdaje za komercijalnu upotrebu.',
  'exports.commercialDisabled': 'Komercijalno izdavanje je isključeno dok pravna provera ne bude odobrena.',
  'exports.failedTitle': 'Izvoz je odbijen ili nije uspeo',
  'exports.failed': 'Izvoz nije uspeo.',
  'exports.downloadFailed': 'Preuzimanje nije uspelo',

  'exports.details.title': 'O ovom skupu podataka',
  'exports.details.columns': 'Kolone',
  'exports.details.column': 'Kolona',
  'exports.details.type': 'Tip',
  'exports.details.meaning': 'Značenje',
  'exports.details.range': 'Dostupni podaci',
  'exports.details.rangeValue': 'od {first} do {last}',
  'exports.details.periods': 'Sačuvano',
  'exports.details.fresh': 'Poslednje osvežavanje',
  'exports.details.noData': 'Još nema podataka',
  'exports.details.unknown': 'Nepoznato',
  'exports.type.string': 'tekst',
  'exports.type.number': 'broj',
  'exports.type.date': 'datum',
  'exports.type.boolean': 'da ili ne',
  'exports.periods.snapshot_one': '{count} snimak',
  'exports.periods.snapshot_few': '{count} snimka',
  'exports.periods.snapshot_other': '{count} snimaka',
  'exports.periods.week_one': '{count} nedelja',
  'exports.periods.week_few': '{count} nedelje',
  'exports.periods.week_other': '{count} nedelja',
  'exports.periods.month_one': '{count} mesec',
  'exports.periods.month_few': '{count} meseca',
  'exports.periods.month_other': '{count} meseci',
  'exports.periods.day_one': '{count} dan',
  'exports.periods.day_few': '{count} dana',
  'exports.periods.day_other': '{count} dana',

  'exports.warn.beforeFirst': 'Podaci počinju od {date}, pa deo perioda pre tog dana nema podataka.',
  'exports.warn.beforeFirstSnapshot': 'Istorijski snimci pre {date} još ne postoje: nedeljno snimanje je tada počelo. Fajl će sadržati samo ono što postoji.',
  'exports.warn.oneSnapshot': 'Postoji samo jedan snimak ({date}). Trendovi i poređenja kroz vreme traže bar dva snimka, a oni se dodaju svake nedelje.',
  'exports.warn.outside': 'Izabrani period nema podataka. Dostupno: od {first} do {last}',
  'exports.warn.afterLast': 'Najnoviji podaci su od {date}',
  'exports.estimate': 'Procenjen broj redova: {rows}',
  'exports.estimateLoading': 'Procena broja redova',
  'exports.estimateTooMany': 'Procenjen broj redova: {rows}. Jedan izvoz sadrži najviše {max}; suzi period ili dodaj filter.',

  'exports.result.running': 'Izrada se nastavlja u pozadini. Stranica se može napustiti; izvoz se pojavljuje u istoriji kada bude gotov.',
  'exports.result.failed': 'Izvoz nije uspeo.',
  'exports.result.failedReason': 'Izvoz nije uspeo: {reason}.',
  'exports.result.suppressed': ', prikriveno ćelija: {cells}',
  'exports.result.timing': 'Vreme na serveru {server} ms',
  'exports.result.timingHere': ', spremno za {ms} ms ovde',
  'exports.download': 'Preuzmi',

  'exports.datasets.title': 'Skupovi podataka',
  'exports.datasets.description': 'Šta koji skup sadrži, odakle potiče i ko sme da ga dobije.',
  'exports.datasets.count_one': '{count} skup podataka dostupan tvojoj ulozi',
  'exports.datasets.count_few': '{count} skupa podataka dostupna tvojoj ulozi',
  'exports.datasets.count_other': '{count} skupova podataka dostupno tvojoj ulozi',
  'exports.col.dataset': 'Skup podataka',
  'exports.col.class': 'Vrsta',
  'exports.col.grain': 'Jedinica vremena',
  'exports.col.disclosure': 'Zaštita od otkrivanja',
  'exports.col.commercial': 'Komercijalno',
  'exports.col.source': 'Izvor',
  'exports.col.licence': 'Licenca',
  'exports.class.catalog': 'Maloprodajni katalog',
  'exports.class.customer_derived': 'Iz korisničkih podataka',
  'exports.class.operational': 'Operativni',
  'exports.grain.snapshot': 'snimak',
  'exports.grain.week': 'nedelja',
  'exports.grain.month': 'mesec',
  'exports.grain.day': 'dan',
  'exports.commercial.eligible': 'dozvoljeno',
  'exports.commercial.afterReview': 'dozvoljeno posle provere',
  'exports.commercial.never': 'nikada',

  'exports.history.title': 'Istorija i revizioni dnevnik',
  'exports.history.all': 'Svaki zahtev za izvoz, odbijanje i preuzimanje svih korisnika.',
  'exports.history.own': 'Tvoji zahtevi za izvoz, odbijanja i preuzimanja.',
  'exports.history.empty': 'Još nema izvoza.',
  'exports.history.when': 'Vreme',
  'exports.history.event': 'Događaj',
  'exports.history.dataset': 'Skup podataka',
  'exports.history.audience': 'Namena',
  'exports.history.format': 'Format',
  'exports.history.rows': 'Redova',
  'exports.history.user': 'Korisnik',
  'exports.history.purpose': 'Svrha',
  'exports.event.requested': 'zatražen',
  'exports.event.completed': 'završen',
  'exports.event.failed': 'neuspešan',
  'exports.event.downloaded': 'preuzet',
  'exports.event.refused': 'odbijen',
};
